/*
  Say whether the phone line is open, in the shopper's own time rather than the shop's.

  Liquid cannot answer this. It renders on Shopify's servers, in the shop's timezone, and
  the result is cached, so "open now" baked into the markup is wrong for anyone in another
  timezone and wrong for everyone an hour later. The hours are attributes on the element and
  the arithmetic happens here, against the store's stated Eastern hours whatever the
  shopper's own clock says.

  The markup ships with the hours and no state, so a shopper with no JavaScript reads
  "Mon to Thu 8am to 5pm EST" and is told nothing untrue. This only ever adds.

  Hours are the store's own, from /pages/contact: Monday to Thursday 8AM-5PM EST, Friday
  8AM-4PM EST, closed Saturday and Sunday. Friday is 4:30pm since 2026-10-13, from customer
  service by way of Shawna; the contact page itself still says 4PM.
*/
(() => {
  const OPEN_LABEL = 'Open now';
  const SHUT_LABEL = 'Closed now';

  /* The store's local time, as hours with a fraction (4:30pm is 16.5), and weekday,
     wherever the shopper is reading from. */
  const storeNow = (zone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(new Date());
    const value = (type) => (parts.find((p) => p.type === type) || {}).value;
    const days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    /* Intl renders midnight as 24 in some engines under hour12: false. */
    const hour = (parseInt(value('hour'), 10) % 24) + parseInt(value('minute'), 10) / 60;
    return { day: days[value('weekday')], hour };
  };

  const isOpen = (el) => {
    const zone = el.dataset.zone || 'America/New_York';
    let now;
    try {
      now = storeNow(zone);
    } catch (e) {
      /* An engine without that timezone should say nothing rather than guess. */
      return null;
    }
    if (Number.isNaN(now.hour) || now.day === undefined) return null;
    if (now.day === 0 || now.day === 6) return false;
    /* Hours with a fraction: Friday closes at 16.5, 4:30pm (Shawna, 2026-10-13). */
    const open = parseFloat(el.dataset.weekdayOpen);
    const close = now.day === 5
      ? parseFloat(el.dataset.fridayClose)
      : parseFloat(el.dataset.weekdayClose);
    if (Number.isNaN(open) || Number.isNaN(close)) return null;
    return now.hour >= open && now.hour < close;
  };

  const paint = (el) => {
    const state = el.querySelector('[data-atlas-concierge-state]');
    if (!state) return;
    const open = isOpen(el);
    if (open === null) return;
    state.textContent = open ? OPEN_LABEL : SHUT_LABEL;
    state.className = open ? 'atlas-conc__state atlas-conc__state--open' : 'atlas-conc__state';
  };

  const paintAll = () =>
    document.querySelectorAll('[data-atlas-concierge-hours]').forEach(paint);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  } else {
    paintAll();
  }
  /* Dawn replaces the buy column on a variant change, which takes this with it. */
  document.addEventListener('shopify:section:load', paintAll);
  window.addEventListener('pageshow', paintAll);
})();
